/**
 * Resumen Unificado de Finanzas — Nuevo dashboard 360º.
 * Consume `/api/finanzas/resumen-completo` y muestra 5 bloques:
 *   1) Volumen total
 *   2) Liquidaciones B2B con aging
 *   3) Cartera de cobro
 *   4) Evolución mensual
 *   5) Top clientes / proveedores / aseguradoras
 */
import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Clock,
  FileText, Building2, Users, ArrowRight, Wallet,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import API from '@/lib/api';
import { toast } from 'sonner';
import FinanzasNav from '@/components/finanzas/FinanzasNav';

const fmtEUR = (n) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

function KPICard({ title, value, sub, icon: Icon, color = 'primary', testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
          {Icon && <Icon className={`w-4 h-4 text-${color}`} />}
        </div>
        <p className="text-2xl font-bold leading-none">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function AgingBar({ label, importe, n, color }) {
  return (
    <div className="flex items-center justify-between p-2 rounded border bg-muted/30">
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold">{fmtEUR(importe)}</p>
        <p className="text-[10px] text-muted-foreground">{n} siniestro{n !== 1 ? 's' : ''}</p>
      </div>
    </div>
  );
}

export default function FinanzasResumen() {
  const [periodo, setPeriodo] = useState('mes');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.get(`/finanzas/resumen-completo?periodo=${periodo}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Error cargando resumen'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [periodo]);

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold">Finanzas</h1>
          <p className="text-sm text-muted-foreground">Vista 360º de facturación, cobros, liquidaciones y comisiones</p>
        </div>
        <Tabs value={periodo} onValueChange={setPeriodo}>
          <TabsList data-testid="periodo-tabs">
            <TabsTrigger value="mes">Mes</TabsTrigger>
            <TabsTrigger value="trimestre">Trimestre</TabsTrigger>
            <TabsTrigger value="año">Año</TabsTrigger>
            <TabsTrigger value="total">Total</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <FinanzasNav />

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : (
        <>
          {/* === 1) VOLUMEN TOTAL === */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Volumen del periodo
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPICard
                title="Facturado total"
                value={fmtEUR(data.volumen.facturado_total)}
                sub={`${data.volumen.n_facturas} factura${data.volumen.n_facturas !== 1 ? 's' : ''}`}
                icon={FileText}
                color="primary"
                testId="kpi-facturado"
              />
              <KPICard
                title="Cobrado"
                value={fmtEUR(data.volumen.cobrado)}
                sub={data.volumen.facturado_total
                  ? `${Math.round((data.volumen.cobrado / data.volumen.facturado_total) * 100)}% del total`
                  : '—'}
                icon={CheckCircle2}
                color="emerald-600"
                testId="kpi-cobrado"
              />
              <KPICard
                title="Pendiente cobro"
                value={fmtEUR(data.volumen.pendiente)}
                sub="Facturas emitidas o vencidas"
                icon={Clock}
                color="amber-600"
                testId="kpi-pendiente"
              />
              <KPICard
                title="% B2B / Particulares"
                value={`${data.volumen.pct_b2b}% / ${(100 - data.volumen.pct_b2b).toFixed(1)}%`}
                sub={`${fmtEUR(data.volumen.facturado_b2b)} B2B · ${fmtEUR(data.volumen.facturado_particulares)} Part.`}
                icon={Building2}
                color="primary"
                testId="kpi-b2b"
              />
            </div>
          </section>

          {/* === 2) LIQUIDACIONES B2B === */}
          <section className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Liquidaciones B2B
              </h2>
              <Link to="/crm/finanzas/liquidaciones" className="text-xs text-primary hover:underline flex items-center gap-1">
                Ver detalle <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Aging */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Antigüedad de pendientes</CardTitle>
                  <CardDescription className="text-xs">
                    Total pendiente: <strong>{fmtEUR(data.liquidaciones.pendiente_total)}</strong>
                    {' · '}{data.liquidaciones.pendiente_n} siniestros
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2" data-testid="aging-block">
                  <AgingBar label="0-30 días" importe={data.liquidaciones.aging['0_30']} n={data.liquidaciones.aging['0_30_n']} color="bg-emerald-500" />
                  <AgingBar label="31-60 días" importe={data.liquidaciones.aging['31_60']} n={data.liquidaciones.aging['31_60_n']} color="bg-amber-500" />
                  <AgingBar label="61-90 días" importe={data.liquidaciones.aging['61_90']} n={data.liquidaciones.aging['61_90_n']} color="bg-orange-500" />
                  <AgingBar label="+90 días ⚠️" importe={data.liquidaciones.aging.mas_90} n={data.liquidaciones.aging.mas_90_n} color="bg-red-500" />
                </CardContent>
              </Card>

              {/* Top aseguradoras */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Top aseguradoras pendientes</CardTitle>
                  <CardDescription className="text-xs">Pendientes de liquidación por compañía</CardDescription>
                </CardHeader>
                <CardContent>
                  {data.liquidaciones.top_aseguradoras.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin pendientes</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="top-aseguradoras">
                      {data.liquidaciones.top_aseguradoras.map((a, i) => (
                        <div key={a.compania} className="flex items-center justify-between p-2 rounded hover:bg-muted/50">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">#{i + 1}</Badge>
                            <span className="text-sm font-medium truncate max-w-[200px]">{a.compania}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold">{fmtEUR(a.importe)}</p>
                            <p className="text-[10px] text-muted-foreground">{a.n} siniestros</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-4 mt-3 pt-3 border-t text-xs">
                    <span><strong className="text-emerald-700">{data.liquidaciones.pagado_n}</strong> pagadas ({fmtEUR(data.liquidaciones.pagado_total)})</span>
                    <span><strong className="text-amber-700">{data.liquidaciones.reclamados_n}</strong> reclamadas</span>
                    <span><strong className="text-blue-700">{data.liquidaciones.garantias_n}</strong> garantías</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* === 3) CARTERA DE COBRO === */}
          <section className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Wallet className="w-5 h-5 text-purple-600" />
                Cartera de cobro
              </h2>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    const t = toast.loading('Generando albaranes…');
                    try {
                      const { data: r } = await API.post('/contabilidad/batch/albaranar-ordenes-cerradas');
                      toast.dismiss(t);
                      toast.success(r.mensaje);
                      setPeriodo((p) => p); // trigger reload (no-op pero refresca)
                      const { data: nuevo } = await API.get(`/finanzas/resumen-completo?periodo=${periodo}`);
                      setData(nuevo);
                    } catch (e) {
                      toast.dismiss(t);
                      toast.error(e?.response?.data?.detail || 'Error');
                    }
                  }}
                  data-testid="btn-albaranar-todas"
                >
                  📋 Albaranar OTs cerradas
                </Button>
                <Button
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm('¿Facturar todos los albaranes pendientes? Se generará 1 factura agrupada por cliente.')) return;
                    const t = toast.loading('Facturando albaranes…');
                    try {
                      const { data: r } = await API.post('/contabilidad/batch/facturar-albaranes-pendientes');
                      toast.dismiss(t);
                      toast.success(r.mensaje);
                      const { data: nuevo } = await API.get(`/finanzas/resumen-completo?periodo=${periodo}`);
                      setData(nuevo);
                    } catch (e) {
                      toast.dismiss(t);
                      toast.error(e?.response?.data?.detail || 'Error');
                    }
                  }}
                  data-testid="btn-facturar-todos"
                >
                  💰 Facturar pendientes
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-amber-300 bg-amber-50/40">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs uppercase text-amber-700">Sin facturar</p>
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold">{data.cartera.ordenes_sin_factura}</p>
                  <p className="text-xs text-muted-foreground mt-1">OT cerradas pendientes albarán/factura</p>
                  <Link to="/crm/finanzas/facturas" className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1">
                    Facturar <ArrowRight className="w-3 h-3" />
                  </Link>
                </CardContent>
              </Card>
              <Card className="border-red-300 bg-red-50/40">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs uppercase text-red-700">Facturas vencidas</p>
                    <TrendingDown className="w-4 h-4 text-red-600" />
                  </div>
                  <p className="text-2xl font-bold">{fmtEUR(data.cartera.facturas_vencidas_importe)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{data.cartera.facturas_vencidas_n} facturas</p>
                </CardContent>
              </Card>
              <Card className="border-blue-300 bg-blue-50/40">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs uppercase text-blue-700">Comisiones pendientes</p>
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold">{fmtEUR(data.comisiones.pendiente_total)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{data.comisiones.pendiente_n} por liquidar a técnicos</p>
                  <Link to="/crm/finanzas/comisiones" className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1">
                    Gestionar <ArrowRight className="w-3 h-3" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* === 4) EVOLUCIÓN === */}
          <section className="mb-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Evolución últimos 12 meses</CardTitle>
                <CardDescription>Facturado vs Cobrado vs Pendiente</CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <LineChart data={data.evolucion}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="nombre_mes" fontSize={11} />
                      <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => fmtEUR(v)} />
                      <Legend />
                      <Line type="monotone" dataKey="facturado" stroke="#3b82f6" strokeWidth={2} name="Facturado" />
                      <Line type="monotone" dataKey="cobrado" stroke="#10b981" strokeWidth={2} name="Cobrado" />
                      <Line type="monotone" dataKey="pendiente" stroke="#f59e0b" strokeWidth={2} name="Pendiente" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* === 5) TOP MOVIMIENTOS === */}
          <section className="grid md:grid-cols-2 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top clientes (por facturación)</CardTitle>
              </CardHeader>
              <CardContent>
                {data.top.clientes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin datos en este periodo</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.top.clientes.map((c, i) => (
                      <div key={`${c.nombre}-${i}`} className="flex items-center justify-between p-2 hover:bg-muted/50 rounded">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">#{i + 1}</Badge>
                          <span className="text-sm truncate max-w-[200px]">{c.nombre}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">{fmtEUR(c.total)}</p>
                          <p className="text-[10px] text-muted-foreground">{c.n} factura{c.n !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top proveedores (por gasto)</CardTitle>
              </CardHeader>
              <CardContent>
                {data.top.proveedores.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin datos en este periodo</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.top.proveedores.map((p, i) => (
                      <div key={`${p.proveedor}-${i}`} className="flex items-center justify-between p-2 hover:bg-muted/50 rounded">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">#{i + 1}</Badge>
                          <span className="text-sm truncate max-w-[200px]">{p.proveedor}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">{fmtEUR(p.total)}</p>
                          <p className="text-[10px] text-muted-foreground">{p.n} compra{p.n !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}
